import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
// City size track (ECONOMY v2): Small / Medium / Big bands with a marker showing the
// player's current population; under each band its round income and repair multiplier.
const rangeText = (b) => (b.max === null || b.max === undefined ? `${b.min}+` : `${b.min}–${b.max}`);

function markerPct(brackets, pop) {
  const n = brackets.length;
  if (!n) return 0;
  let idx = 0;
  brackets.forEach((b, i) => {
    if (pop >= b.min) idx = i;
  });
  const b = brackets[idx];
  const prev = brackets[idx - 1];
  const open = b.max === null || b.max === undefined;
  const span = open ? Math.max(1, prev ? b.min - prev.min : 5) : b.max - b.min + 1;
  const within = Math.min(0.88, Math.max(0.12, (pop - b.min + 0.5) / span));
  return ((idx + within) / n) * 100;
}

const COLS = (n) => ({ gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))`, columnGap: 3 });

// TERRAIN 3 of 3: population = House Cubes + connected buildings (n - 1 per group) + the coastal
// bonus (+1 per cube on a coastal square) - 1 per Sea Wall (never below 0). One line per part.
export function populationLines(economy) {
  if (!economy) return [];
  const lines = [
    { key: "cubes", label: "House Cubes", value: `${economy.cubes ?? 0}` },
    { key: "groups", label: "Connected", value: `+${economy.groups ?? 0}` },
    { key: "coastal", label: "Coastal", value: `+${economy.coastal ?? 0}` },
  ];
  if (economy.walls) lines.push({ key: "walls", label: "Sea walls", value: `−${economy.walls}` });
  return lines;
}

export function PopulationBreakdown({ color, economy, style }) {
  const lines = populationLines(economy);
  if (!lines.length) return null;
  return (
    <div data-testid={`player-population-breakdown-${color}`} className="flex flex-col" style={{ gap: 1, fontSize: 12, color: "var(--ink-2)", ...style }}>
      <div className="flex items-center justify-between" style={{ fontWeight: 700, color: "var(--ink)" }}>
        <span>Population</span>
        <span className="tabular-nums" data-testid={`player-pop-total-${color}`}>{economy.population ?? 0}</span>
      </div>
      {lines.map((ln) => (
        <div key={ln.key} data-testid={`player-pop-${ln.key}-${color}`} className="flex items-center justify-between">
          <span>{ln.label}</span>
          <span className="tabular-nums">{ln.value}</span>
        </div>
      ))}
    </div>
  );
}

export default function CitySizeTrack({ color, economy, brackets, breakdown = "title" }) {
  if (!brackets?.length) return null;
  const pop = economy?.population ?? 0;
  const current = economy?.bracket ?? brackets[0].key;
  const pct = markerPct(brackets, pop);
  const title = economy
    ? `Population ${pop}: ${populationLines(economy).map((l) => `${l.label} ${l.value}`).join(", ")}. ${economy.bracket_name} city: +${economy.income} money per round, repairs ×${economy.repair_multiplier}.`
    : `Population ${pop}`;
  const track = (
    <div data-testid={`city-track-${color}`} data-bracket={current} className="flex flex-col" style={{ gap: 1 }} title={breakdown === "title" ? title : undefined}>
      <div className="grid" style={COLS(brackets.length)}>
        {brackets.map((b) => {
          const on = b.key === current;
          return (
            <span key={b.key} data-testid={`city-band-label-${color}-${b.key}`} className="whitespace-nowrap overflow-hidden" style={{ fontSize: 10, lineHeight: "12px", letterSpacing: "-0.01em", fontWeight: on ? 700 : 500, color: on ? "var(--ink)" : "var(--ink-4)" }}>
              {b.name} {rangeText(b)}
            </span>
          );
        })}
      </div>
      <div className="relative grid items-center" style={{ ...COLS(brackets.length), height: 16 }}>
        {brackets.map((b) => (
          <span
            key={b.key}
            data-testid={`city-band-${color}-${b.key}`}
            data-active={b.key === current ? "true" : "false"}
            style={{ height: 6, borderRadius: 3, background: b.key === current ? "var(--sea)" : "var(--line)" }}
          />
        ))}
        <span
          data-testid={`player-population-${color}`}
          aria-label={`Population ${pop}`}
          className="absolute inline-flex items-center justify-center tabular-nums"
          style={{
            left: `${pct}%`,
            top: 0,
            minWidth: 18,
            height: 16,
            padding: "0 4px",
            transform: "translateX(-50%)",
            borderRadius: 8,
            background: "var(--ink)",
            color: "var(--card)",
            fontSize: 10.5,
            fontWeight: 700,
            lineHeight: "16px",
            transition: "left 400ms cubic-bezier(.4,0,.2,1)",
          }}
        >
          {pop}
        </span>
      </div>
      <div className="grid" style={COLS(brackets.length)}>
        {brackets.map((b) => (
          <span
            key={b.key}
            data-testid={`city-band-terms-${color}-${b.key}`}
            title={`${b.name} city: +${b.income} money per round, repairs ×${b.repair_multiplier}`}
            className="whitespace-nowrap overflow-hidden tabular-nums"
            style={{ fontSize: 10, lineHeight: "12px", color: b.key === current ? "var(--ink-2)" : "var(--ink-4)", fontWeight: b.key === current ? 600 : 400 }}
          >
            +{b.income} · ×{b.repair_multiplier}
          </span>
        ))}
      </div>
    </div>
  );
  if (breakdown !== "hover" || !economy) return track;
  return (
    <HoverCard openDelay={120} closeDelay={80}>
      <HoverCardTrigger asChild>{track}</HoverCardTrigger>
      <HoverCardContent side="right" align="start" sideOffset={10} className="w-[200px] p-3" style={{ background: "var(--card)", borderColor: "var(--line)", boxShadow: "var(--shadow-card)" }}>
        <PopulationBreakdown color={color} economy={economy} />
      </HoverCardContent>
    </HoverCard>
  );
}
